import React, { useEffect, useState } from "react";
import {
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

export default function MonthlyRadarChart({ years, selectedGlobalYear }) {
    const history = useHistory();

    const [isDarkMode, setIsDarkMode] = useState(false);
    const [selectedYear, setSelectedYear] = useState(selectedGlobalYear || new Date().getFullYear());
    const [chartData, setChartData] = useState([]);

    // 🔐 LOGIN CHECK
    useEffect(() => {
        if (!localStorage.getItem("userData")) {
            history.push("/login");
        }
    }, [history]);

    // GET USER DATA
    const storedUserData = localStorage.getItem("userData");
    let userId = "";
    let groupId = "";

    if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        userId = userData.userId;
        groupId = userData.groupId;
    }
    
  useEffect(() => {
    setSelectedYear(selectedGlobalYear); // Sync with global year when it changes
  }, [selectedGlobalYear]);

    // 🌙 DARK MODE DETECTION
    useEffect(() => {
        const checkDarkMode = () =>
            document.documentElement.classList.contains("dark");

        setIsDarkMode(checkDarkMode());

        const observer = new MutationObserver(() => {
            setIsDarkMode(checkDarkMode());
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });

        return () => observer.disconnect();
    }, []);

    // 📊 FETCH YEARLY EXPENSE DATA
    useEffect(() => {
        if (!groupId) return;

        const fetchData = async () => {
            try {
                const data = await apiService.getYearlyExpense(groupId, selectedYear);

                const months = [
                    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
                ];

                const monthlyMap = {};
                months.forEach((m) => {
                    monthlyMap[m] = { income: 0, expenses: 0 };
                });

                data.forEach((item) => {
                    const monthName = months[parseInt(item.month) - 1];
                    monthlyMap[monthName].income += Number(item.totalCredit || 0);
                    monthlyMap[monthName].expenses += Number(item.totalDebit || 0);
                });

                const transformedData = months.map((m) => ({
                    month: m,
                    income: monthlyMap[m].income,
                    expenses: monthlyMap[m].expenses,
                }));

                setChartData(transformedData);
            } catch (err) {
                console.error("Error loading radar chart data:", err);
            }
        };

        fetchData();
    }, [selectedYear, groupId]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div
                    style={{
                        backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                        borderRadius: 8,
                        color: isDarkMode ? "#f9fafb" : "#111827",
                        border: "none",
                        padding: "10px",
                    }}
                >
                    <p style={{ margin: "0 0 10px 0", fontWeight: "bold" }}>{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ margin: 0, color: entry.color }}>
                            {entry.dataKey}: {entry.value.toLocaleString()}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 w-full">

            {/* Header */}
             <div className="flex justify-between items-center mb-4 flex-row lg:flex-row sm:flex-col">
         <h2 className="text-gray-800 dark:text-white text-base font-semibold">
                    Monthly Credit vs Debit
                </h2>

                <select
                    className="text-xs text-gray-800 dark:text-white rounded-md px-3 py-2"
                    style={{ minWidth: "80px", border: "solid #ddd 1px" }}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                    {years.map((year) => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </select>
      </div>

            {/* Chart */}
            <div style={{ height: 410 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="75%" data={chartData}>

                        <PolarGrid strokeDasharray="4 4" />

                        <PolarAngleAxis
                            dataKey="month"
                            tick={{ fill: isDarkMode ? "#d1d5db" : "#6b7280", fontSize: 12 }}
                        />

                        <PolarRadiusAxis
                            angle={30}
                            tick={{ fill: isDarkMode ? "#d1d5db" : "#9ca3af", fontSize: 11 }}
                            tickFormatter={(value) => value.toLocaleString()}
                        />

                        <Tooltip
                            content={<CustomTooltip />}
                            wrapperStyle={{
                                backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                                borderRadius: 8,
                                color: isDarkMode ? "#f9fafb" : "#111827",
                                border: "none",
                            }}
                            contentStyle={{
                                backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                                border: "none",
                                fontSize: 12,
                            }}
                        />

                        <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="circle"
                            formatter={(value) => (
                                <span style={{ color: isDarkMode ? "#f9fafb" : "#111827" }}>
                                    {value}
                                </span>
                            )}
                        />

                        {/* ========== SAME COLORS AS YOUR NEXT.JS COMPONENT ========== */}
                        <Radar
                            name="Credit"
                            dataKey="income"
                            stroke="#60d394"
                            fill="#60d394"
                            fillOpacity={0.4}
                        />

                        <Radar
                            name="Debit"
                            dataKey="expenses"
                            stroke="#ef4444"
                            fill="#ef4444"
                            fillOpacity={0.4}
                        />

                    </RadarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}